import React, { useState } from "react";
import Header from "../components/Header";
import Footer from "../components/Footer";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import { Textarea } from "../components/ui/textarea";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  CardDescription,
} from "../components/ui/card";
import {
  ArrowRight,
  ArrowLeft,
  Loader2,
  Star,
  Check,
  User,
  GraduationCap,
  Lightbulb,
  Briefcase,
  Brain,
} from "lucide-react";

const TOTAL_STEPS = 5;

const stepMeta = [
  { icon: User, label: "About You", desc: "Basic information about you and your practice" },
  { icon: GraduationCap, label: "Education", desc: "Your academic and professional background" },
  { icon: Lightbulb, label: "Your Vision", desc: "Why you'd be a great fit for the program" },
  { icon: Briefcase, label: "Experience", desc: "Your advisory and consulting background" },
  { icon: Brain, label: "AI Perspective", desc: "Your vision for AI in dentistry" },
];

const AIFellowsApplicationPage = () => {
  const [step, setStep] = useState(1);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [formData, setFormData] = useState({
    firstName: "",
    lastName: "",
    email: "",
    phone: "",
    practiceName: "",
    practiceCity: "",
    practiceState: "",
    providers: "",
    locations: "",
    role: "",
    currentPMS: "",
    school: "",
    degree: "",
    graduationYear: "",
    additionalCerts: "",
    whyGreatFit: "",
    topThreeGoals: "",
    priorAdvisory: "",
    advisoryDetails: "",
    aiTransformPriorities: "",
    heardAbout: "",
  });

  const handleInputChange = (e) => {
    const { id, value } = e.target;
    setFormData((prev) => ({ ...prev, [id]: value }));
  };

  const handleSelectChange = (field, value) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  const handleNext = () => {
    if (step < TOTAL_STEPS) setStep(step + 1);
  };

  const handleBack = () => {
    if (step > 1) setStep(step - 1);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      const isLocalhost =
        typeof window !== "undefined" &&
        (window.location.hostname === "localhost" ||
          window.location.hostname === "127.0.0.1");
      const apiUrl =
        process.env.REACT_APP_API_URL ||
        (isLocalhost ? "http://localhost:8001" : "");

      const response = await fetch(`${apiUrl}/api/fellows-application`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(formData),
      });

      if (!response.ok) {
        throw new Error("Failed to submit application");
      }

      setIsSubmitted(true);
    } catch (error) {
      console.error("Error submitting application:", error);
      alert("There was an error submitting your application. Please try again.");
      setIsSubmitting(false);
    }
  };

  if (isSubmitted) {
    return (
      <div className="min-h-screen bg-white">
        <Header />
        <div className="bg-gradient-to-br from-blue-50 via-white to-indigo-50/50 flex items-center justify-center px-4 pt-32 pb-20">
          <Card className="shadow-2xl border-0 ring-1 ring-slate-900/5 bg-white/90 backdrop-blur-sm overflow-hidden max-w-lg w-full">
            <div className="h-1.5 bg-gradient-to-r from-blue-500 via-indigo-500 to-blue-600" />
            <div className="p-10 text-center">
              <div className="w-16 h-16 rounded-2xl bg-emerald-100 flex items-center justify-center mx-auto mb-6 border border-emerald-200">
                <Check className="w-8 h-8 text-emerald-600" />
              </div>
              <h2 className="text-2xl font-bold text-slate-900 mb-3">
                Application Received
              </h2>
              <p className="text-slate-500 leading-relaxed mb-6">
                Thank you for applying to the AI Innovation Fellows program. Your
                application will be reviewed within 1-2 weeks. You may be invited
                to an interview with the program leader, the CMO of the MSU Health
                System.
              </p>
              <Button
                onClick={() => (window.location.href = "/ai-fellows")}
                className="bg-blue-600 hover:bg-blue-700 text-white font-semibold rounded-lg shadow-lg shadow-blue-200"
              >
                Back to Fellows Overview
                <ArrowRight className="ml-2 w-4 h-4" />
              </Button>
            </div>
          </Card>
        </div>
        <Footer />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-white">
      <Header />
    <div className="bg-gradient-to-br from-blue-50 via-white to-indigo-50/50 relative overflow-hidden">
      <style>{`
        @keyframes blob {
          0% { transform: translate(0px, 0px) scale(1); }
          33% { transform: translate(30px, -50px) scale(1.1); }
          66% { transform: translate(-20px, 20px) scale(0.9); }
          100% { transform: translate(0px, 0px) scale(1); }
        }
        .animate-blob { animation: blob 7s infinite; }
        .animation-delay-2000 { animation-delay: 2s; }
        .animation-delay-4000 { animation-delay: 4s; }
      `}</style>

      {/* Background blobs */}
      <div className="absolute top-0 left-0 w-full h-full overflow-hidden -z-10">
        <div className="absolute top-0 right-0 w-[500px] h-[500px] bg-blue-100/40 rounded-full mix-blend-multiply filter blur-3xl opacity-70 animate-blob" />
        <div className="absolute top-0 -left-4 w-[500px] h-[500px] bg-purple-100/40 rounded-full mix-blend-multiply filter blur-3xl opacity-70 animate-blob animation-delay-2000" />
        <div className="absolute -bottom-8 left-20 w-[500px] h-[500px] bg-indigo-100/40 rounded-full mix-blend-multiply filter blur-3xl opacity-70 animate-blob animation-delay-4000" />
      </div>

      <div className="container mx-auto max-w-3xl px-4 pt-28 pb-16">
        {/* Header */}
        <div className="text-center mb-8">
          <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-blue-50 text-blue-600 text-sm font-semibold mb-4 border border-blue-100/60 shadow-sm">
            <Star className="w-4 h-4" />
            <span>AI Innovation Fellows</span>
          </div>
          <h1 className="text-3xl md:text-4xl font-bold text-slate-900 tracking-tight">
            Fellowship Application
          </h1>
        </div>

        {/* Step indicator */}
        <div className="flex items-center justify-center gap-2 mb-8">
          {stepMeta.map((s, i) => {
            const StepIcon = s.icon;
            const isActive = step === i + 1;
            const isComplete = step > i + 1;
            return (
              <React.Fragment key={i}>
                <button
                  onClick={() => {
                    if (isComplete) setStep(i + 1);
                  }}
                  className={`flex items-center gap-2 px-3 py-2 rounded-xl text-xs font-semibold transition-all ${
                    isActive
                      ? "bg-blue-600 text-white shadow-lg shadow-blue-200"
                      : isComplete
                      ? "bg-blue-50 text-blue-600 border border-blue-100 cursor-pointer hover:bg-blue-100"
                      : "bg-slate-100 text-slate-400 border border-slate-200"
                  }`}
                >
                  {isComplete ? (
                    <Check className="w-3.5 h-3.5" />
                  ) : (
                    <StepIcon className="w-3.5 h-3.5" />
                  )}
                  <span className="hidden md:inline">{s.label}</span>
                  <span className="md:hidden">{i + 1}</span>
                </button>
                {i < TOTAL_STEPS - 1 && (
                  <div
                    className={`w-6 h-0.5 rounded-full ${
                      step > i + 1 ? "bg-blue-400" : "bg-slate-200"
                    }`}
                  />
                )}
              </React.Fragment>
            );
          })}
        </div>

        {/* Form card */}
        <Card className="shadow-2xl border-0 ring-1 ring-slate-900/5 bg-white/90 backdrop-blur-sm overflow-hidden">
          <div className="h-1.5 bg-gradient-to-r from-blue-500 via-indigo-500 to-blue-600" />
          <CardHeader className="pb-4 pt-6 px-6">
            <CardTitle className="text-xl font-bold text-slate-900">
              {stepMeta[step - 1].label}
            </CardTitle>
            <CardDescription className="text-slate-500 text-sm mt-1">
              {stepMeta[step - 1].desc}
            </CardDescription>
          </CardHeader>
          <CardContent className="px-6 pb-6">
            <form
              onSubmit={(e) => {
                e.preventDefault();
                if (step < TOTAL_STEPS) handleNext();
                else handleSubmit(e);
              }}
            >
              {/* STEP 1: Demographics */}
              {step === 1 && (
                <div className="space-y-3">
                  <div className="grid grid-cols-2 gap-3">
                    <div className="space-y-1.5">
                      <Label htmlFor="firstName" className="text-xs font-semibold text-slate-700">First name *</Label>
                      <Input id="firstName" placeholder="Jane" value={formData.firstName} onChange={handleInputChange} required className="bg-slate-50 border-slate-200 focus:bg-white focus:border-blue-500 transition-all h-9 text-sm" />
                    </div>
                    <div className="space-y-1.5">
                      <Label htmlFor="lastName" className="text-xs font-semibold text-slate-700">Last name *</Label>
                      <Input id="lastName" placeholder="Doe" value={formData.lastName} onChange={handleInputChange} required className="bg-slate-50 border-slate-200 focus:bg-white focus:border-blue-500 transition-all h-9 text-sm" />
                    </div>
                  </div>

                  <div className="space-y-1.5">
                    <Label htmlFor="email" className="text-xs font-semibold text-slate-700">Email *</Label>
                    <Input id="email" type="email" placeholder="jane@practice.com" value={formData.email} onChange={handleInputChange} required className="bg-slate-50 border-slate-200 focus:bg-white focus:border-blue-500 transition-all h-9 text-sm" />
                  </div>

                  <div className="space-y-1.5">
                    <Label htmlFor="phone" className="text-xs font-semibold text-slate-700">Phone number *</Label>
                    <Input id="phone" type="tel" placeholder="(555) 000-0000" value={formData.phone} onChange={handleInputChange} required className="bg-slate-50 border-slate-200 focus:bg-white focus:border-blue-500 transition-all h-9 text-sm" />
                  </div>

                  <div className="space-y-1.5">
                    <Label htmlFor="practiceName" className="text-xs font-semibold text-slate-700">Practice name *</Label>
                    <Input id="practiceName" placeholder="Bright Smile Dental" value={formData.practiceName} onChange={handleInputChange} required className="bg-slate-50 border-slate-200 focus:bg-white focus:border-blue-500 transition-all h-9 text-sm" />
                  </div>

                  <div className="grid grid-cols-2 gap-3">
                    <div className="space-y-1.5">
                      <Label htmlFor="practiceCity" className="text-xs font-semibold text-slate-700">City *</Label>
                      <Input id="practiceCity" placeholder="San Francisco" value={formData.practiceCity} onChange={handleInputChange} required className="bg-slate-50 border-slate-200 focus:bg-white focus:border-blue-500 transition-all h-9 text-sm" />
                    </div>
                    <div className="space-y-1.5">
                      <Label htmlFor="practiceState" className="text-xs font-semibold text-slate-700">State *</Label>
                      <Input id="practiceState" placeholder="CA" value={formData.practiceState} onChange={handleInputChange} required className="bg-slate-50 border-slate-200 focus:bg-white focus:border-blue-500 transition-all h-9 text-sm" />
                    </div>
                  </div>

                  <div className="grid grid-cols-3 gap-3">
                    <div className="space-y-1.5">
                      <Label className="text-xs font-semibold text-slate-700">Providers *</Label>
                      <Select onValueChange={(v) => handleSelectChange("providers", v)}>
                        <SelectTrigger className="bg-slate-50 border-slate-200 focus:bg-white focus:border-blue-500 h-9 text-sm">
                          <SelectValue placeholder="Select..." />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="1-2">1-2</SelectItem>
                          <SelectItem value="3-5">3-5</SelectItem>
                          <SelectItem value="6-10">6-10</SelectItem>
                          <SelectItem value="11+">11+</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="space-y-1.5">
                      <Label className="text-xs font-semibold text-slate-700">Locations *</Label>
                      <Select onValueChange={(v) => handleSelectChange("locations", v)}>
                        <SelectTrigger className="bg-slate-50 border-slate-200 focus:bg-white focus:border-blue-500 h-9 text-sm">
                          <SelectValue placeholder="Select..." />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="1">1</SelectItem>
                          <SelectItem value="2-3">2-3</SelectItem>
                          <SelectItem value="4-10">4-10</SelectItem>
                          <SelectItem value="11+">11+</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="space-y-1.5">
                      <Label className="text-xs font-semibold text-slate-700">Role *</Label>
                      <Select onValueChange={(v) => handleSelectChange("role", v)}>
                        <SelectTrigger className="bg-slate-50 border-slate-200 focus:bg-white focus:border-blue-500 h-9 text-sm">
                          <SelectValue placeholder="Select..." />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="owner">Owner/Dentist</SelectItem>
                          <SelectItem value="associate">Associate Dentist</SelectItem>
                          <SelectItem value="specialist">Specialist</SelectItem>
                          <SelectItem value="manager">Office Manager</SelectItem>
                          <SelectItem value="other">Other</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  </div>

                  <div className="space-y-1.5">
                    <Label htmlFor="currentPMS" className="text-xs font-semibold text-slate-700">Current practice management software</Label>
                    <Input id="currentPMS" placeholder="e.g. Dentrix, Eaglesoft, Open Dental..." value={formData.currentPMS} onChange={handleInputChange} className="bg-slate-50 border-slate-200 focus:bg-white focus:border-blue-500 transition-all h-9 text-sm" />
                  </div>
                </div>
              )}

              {/* STEP 2: Education */}
              {step === 2 && (
                <div className="space-y-3">
                  <div className="space-y-1.5">
                    <Label htmlFor="school" className="text-xs font-semibold text-slate-700">Dental school / university *</Label>
                    <Input id="school" placeholder="e.g. University of Michigan School of Dentistry" value={formData.school} onChange={handleInputChange} required className="bg-slate-50 border-slate-200 focus:bg-white focus:border-blue-500 transition-all h-9 text-sm" />
                  </div>

                  <div className="grid grid-cols-2 gap-3">
                    <div className="space-y-1.5">
                      <Label htmlFor="degree" className="text-xs font-semibold text-slate-700">Degree *</Label>
                      <Select onValueChange={(v) => handleSelectChange("degree", v)}>
                        <SelectTrigger className="bg-slate-50 border-slate-200 focus:bg-white focus:border-blue-500 h-9 text-sm">
                          <SelectValue placeholder="Select..." />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="DDS">DDS</SelectItem>
                          <SelectItem value="DMD">DMD</SelectItem>
                          <SelectItem value="DDS+Specialty">DDS + Specialty</SelectItem>
                          <SelectItem value="DMD+Specialty">DMD + Specialty</SelectItem>
                          <SelectItem value="MBA">MBA</SelectItem>
                          <SelectItem value="Other">Other</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="space-y-1.5">
                      <Label htmlFor="graduationYear" className="text-xs font-semibold text-slate-700">Graduation year *</Label>
                      <Input id="graduationYear" placeholder="e.g. 2015" value={formData.graduationYear} onChange={handleInputChange} required className="bg-slate-50 border-slate-200 focus:bg-white focus:border-blue-500 transition-all h-9 text-sm" />
                    </div>
                  </div>

                  <div className="space-y-1.5">
                    <Label htmlFor="additionalCerts" className="text-xs font-semibold text-slate-700">
                      Additional certifications, specializations, or affiliations <span className="text-slate-400 font-normal">(Optional)</span>
                    </Label>
                    <Textarea id="additionalCerts" placeholder="e.g. Board certified in Periodontics, Fellow of the AGD, member of ADA..." value={formData.additionalCerts} onChange={handleInputChange} className="bg-slate-50 border-slate-200 focus:bg-white focus:border-blue-500 min-h-[80px] resize-none transition-all text-sm" />
                  </div>
                </div>
              )}

              {/* STEP 3: Vision / Fit */}
              {step === 3 && (
                <div className="space-y-4">
                  <div className="space-y-1.5">
                    <Label htmlFor="whyGreatFit" className="text-xs font-semibold text-slate-700">
                      What makes you a great fit and asset for the AI Innovation Fellows program? *
                    </Label>
                    <Textarea id="whyGreatFit" placeholder="Tell us about your background, mindset, and what you'd bring to the cohort..." value={formData.whyGreatFit} onChange={handleInputChange} required className="bg-slate-50 border-slate-200 focus:bg-white focus:border-blue-500 min-h-[120px] resize-none transition-all text-sm" />
                  </div>

                  <div className="space-y-1.5">
                    <Label htmlFor="topThreeGoals" className="text-xs font-semibold text-slate-700">
                      Should you join the program, what would be the top 3 things you'd like to get out of it? *
                    </Label>
                    <Textarea id="topThreeGoals" placeholder="1. &#10;2. &#10;3. " value={formData.topThreeGoals} onChange={handleInputChange} required className="bg-slate-50 border-slate-200 focus:bg-white focus:border-blue-500 min-h-[120px] resize-none transition-all text-sm" />
                  </div>
                </div>
              )}

              {/* STEP 4: Advisory Experience */}
              {step === 4 && (
                <div className="space-y-4">
                  <div className="space-y-1.5">
                    <Label className="text-xs font-semibold text-slate-700">
                      Have you had prior experience advising offices or DSOs on operational excellence or technology transformation? *
                    </Label>
                    <Select onValueChange={(v) => handleSelectChange("priorAdvisory", v)}>
                      <SelectTrigger className="bg-slate-50 border-slate-200 focus:bg-white focus:border-blue-500 h-9 text-sm">
                        <SelectValue placeholder="Select..." />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="yes-extensive">Yes, extensively</SelectItem>
                        <SelectItem value="yes-some">Yes, some experience</SelectItem>
                        <SelectItem value="no-interested">No, but I'm interested</SelectItem>
                        <SelectItem value="no">No</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>

                  <div className="space-y-1.5">
                    <Label htmlFor="advisoryDetails" className="text-xs font-semibold text-slate-700">
                      If yes, tell us more about your experience. If no, tell us what interests you about advisory work. *
                    </Label>
                    <Textarea id="advisoryDetails" placeholder="Describe the types of engagements, how many offices you've worked with, what you focused on..." value={formData.advisoryDetails} onChange={handleInputChange} required className="bg-slate-50 border-slate-200 focus:bg-white focus:border-blue-500 min-h-[140px] resize-none transition-all text-sm" />
                  </div>
                </div>
              )}

              {/* STEP 5: AI Perspective */}
              {step === 5 && (
                <div className="space-y-4">
                  <div className="space-y-1.5">
                    <Label htmlFor="aiTransformPriorities" className="text-xs font-semibold text-slate-700">
                      If you had to pick just 3 parts of a dental office for AI to transform, what would they be and why? Share your prioritization framework or mental model. *
                    </Label>
                    <Textarea id="aiTransformPriorities" placeholder="Think about where AI could have the highest impact — and explain how you'd prioritize across front office, clinical, revenue cycle, patient experience, etc." value={formData.aiTransformPriorities} onChange={handleInputChange} required className="bg-slate-50 border-slate-200 focus:bg-white focus:border-blue-500 min-h-[180px] resize-none transition-all text-sm" />
                  </div>

                  <div className="space-y-1.5">
                    <Label className="text-xs font-semibold text-slate-700">How did you hear about the Fellows program?</Label>
                    <Select onValueChange={(v) => handleSelectChange("heardAbout", v)}>
                      <SelectTrigger className="bg-slate-50 border-slate-200 focus:bg-white focus:border-blue-500 h-9 text-sm">
                        <SelectValue placeholder="Select..." />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="asha-team">Asha team member</SelectItem>
                        <SelectItem value="colleague">Colleague / word of mouth</SelectItem>
                        <SelectItem value="social-media">Social media</SelectItem>
                        <SelectItem value="presentation">Asha presentation / demo</SelectItem>
                        <SelectItem value="other">Other</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>
              )}

              {/* Navigation buttons */}
              <div className="flex items-center justify-between mt-6 pt-4 border-t border-slate-100">
                {step > 1 ? (
                  <Button
                    type="button"
                    variant="outline"
                    onClick={handleBack}
                    className="text-slate-600 border-slate-200 hover:bg-slate-50 h-10 px-5 text-sm font-semibold"
                  >
                    <ArrowLeft className="mr-2 w-4 h-4" />
                    Back
                  </Button>
                ) : (
                  <div />
                )}

                <Button
                  type="submit"
                  disabled={isSubmitting}
                  className="bg-blue-600 hover:bg-blue-700 text-white h-10 px-6 text-sm font-semibold rounded-lg shadow-lg shadow-blue-200 hover:shadow-blue-300 transition-all transform hover:-translate-y-0.5 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {isSubmitting ? (
                    <>
                      <Loader2 className="mr-2 w-4 h-4 animate-spin" />
                      Submitting...
                    </>
                  ) : step < TOTAL_STEPS ? (
                    <>
                      Continue
                      <ArrowRight className="ml-2 w-4 h-4" />
                    </>
                  ) : (
                    <>
                      Submit Application
                      <ArrowRight className="ml-2 w-4 h-4" />
                    </>
                  )}
                </Button>
              </div>

              <p className="text-[10px] text-center text-slate-400 mt-4">
                Step {step} of {TOTAL_STEPS} — Your application will be reviewed within 1-2 weeks.
              </p>
            </form>
          </CardContent>
        </Card>
      </div>
    </div>
    <Footer />
    </div>
  );
};

export default AIFellowsApplicationPage;
